/**
 * The list a Movers News cell opens: every headline for one symbol in the
 * cell's window, newest first — time, tag, headline (a link), publisher.
 *
 * The mock draws it as an expanded row under the grid row; AG Grid Community
 * has no detail rows, so it is a popover under the cell instead
 * (`docs/plans/movers.md`, step 7). Dismissed as `TrackListMenu` is, for its
 * reason: anchored to a grid cell, so a scroll, a click outside or Escape
 * closes it rather than leaving it over the wrong row.
 */

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";

import { fetchMoversNews, type MoversArticle } from "../api/symbols";

export interface NewsListPopoverProps {
  symbol: string;
  window: "pre" | "post" | "market";
  anchor: DOMRect;
  onClose: () => void;
}

const ET_CLOCK = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

export function NewsListPopover({ symbol, window: win, anchor, onClose }: NewsListPopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [articles, setArticles] = useState<MoversArticle[] | null | undefined>(null);
  const [pos, setPos] = useState({ x: anchor.left, y: anchor.bottom + 2 });

  useEffect(() => {
    let live = true;
    setArticles(null);
    void fetchMoversNews(symbol, win).then((got) => {
      if (live) setArticles(got);
    });
    return () => {
      live = false;
    };
  }, [symbol, win]);

  // Inside the viewport once the real size is known: under the cell, or above
  // it when there is no room below.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const below = anchor.bottom + 2;
    setPos({
      x: Math.max(4, Math.min(anchor.left, window.innerWidth - width - 4)),
      y: below + height <= window.innerHeight - 4 ? below : Math.max(4, anchor.top - height - 2),
    });
  }, [anchor.left, anchor.bottom, anchor.top, articles]);

  useEffect(() => {
    const outside = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("pointerdown", outside, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", outside, true);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("pointerdown", outside, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", outside, true);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);

  return (
    <div ref={ref} role="dialog" aria-label={`${symbol} headlines`} style={{ ...BOX, left: pos.x, top: pos.y }}>
      <div style={HEAD}>{symbol}</div>
      {articles === null ? (
        <div style={NOTE}>Loading…</div>
      ) : articles === undefined ? (
        <div style={NOTE}>Headlines could not be loaded.</div>
      ) : articles.length === 0 ? (
        <div style={NOTE}>No headlines in this window.</div>
      ) : (
        <ul style={LIST}>
          {articles.map((a, i) => (
            <li key={`${a.at}-${i}`} style={ITEM}>
              <span style={TIME}>{ET_CLOCK.format(a.at)}</span>
              {a.tag && <span style={TAG}>{a.tag}</span>}
              {a.url ? (
                <a href={a.url} target="_blank" rel="noreferrer noopener" style={LINK}>
                  {a.title ?? a.url}
                </a>
              ) : (
                <span>{a.title}</span>
              )}
              {a.publisher && <span style={PUB}> — {a.publisher}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const BOX: CSSProperties = {
  position: "fixed",
  zIndex: 1000,
  width: 460,
  maxWidth: "calc(100vw - 8px)",
  maxHeight: 320,
  overflowY: "auto",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
  fontSize: 12,
  color: "var(--qf-text)",
};
const HEAD: CSSProperties = { padding: "6px 10px", fontWeight: 600, borderBottom: "1px solid var(--qf-border)" };
const NOTE: CSSProperties = { padding: "8px 10px", color: "var(--qf-text-muted)" };
const LIST: CSSProperties = { listStyle: "none", margin: 0, padding: "4px 0" };
const ITEM: CSSProperties = { padding: "4px 10px", lineHeight: "16px" };
const TIME: CSSProperties = { color: "var(--qf-text-muted)", marginRight: 6, fontVariantNumeric: "tabular-nums" };
const TAG: CSSProperties = { color: "var(--qf-text-muted)", fontSize: 11, marginRight: 6 };
const LINK: CSSProperties = { color: "var(--qf-accent)", textDecoration: "none" };
const PUB: CSSProperties = { color: "var(--qf-text-muted)" };
